import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ITotalsServiceSummary,
  SummaryVariant,
  SERVICE_SUMMARY_VARIANTS,
  IEditPricesPermissions
} from './SummaryBanner.types';
import GenerateQuote from './components/GenerateQuote/GenerateQuote';
import { IlogisticServiceDetails } from 'types/services';
import { BordOneToneIcon, NudosButton } from 'components/NudosComponents';
import ApprovedQuote from './components/ApprovedQuote/ApprovedQuote';
import PendingQuote from './components/PendingQuote/PendingQuote';
import RejectedQuote from './components/RejectedQuote/RejectedQuote';
import { getPermissionsByModule } from 'utils/permissions';
import { DTOgetOrderDetailedData } from 'types/orders';
import OrderSummary from './components/OrderSummary/OrderSummary';
import { BACKOFFICE_MODULES } from 'types/configuration.types';
import './SummaryBanner.scss';

interface ISummaryBannerProps {
  summaryTitle: string;
  variant: SummaryVariant;
  id?: number;
  quoteLink?: string;
  totals?: ITotalsServiceSummary;
  LSData?: IlogisticServiceDetails;
  orderData?: DTOgetOrderDetailedData;
}

const SummaryBanner = ({ summaryTitle, variant, id, quoteLink, totals, LSData, orderData }: ISummaryBannerProps) => {
  const history = useHistory();
  const editPricesPermissions = useMemo<IEditPricesPermissions>(() => getPermissionsByModule({moduleName: BACKOFFICE_MODULES.EDIT_PRICES}), []);

  const renderBanner = () => {
    switch (variant) {
      case SERVICE_SUMMARY_VARIANTS.TO_QUOTE:
        return <GenerateQuote id={id} LSData={LSData} editPricesPermissions={editPricesPermissions} />;
      case SERVICE_SUMMARY_VARIANTS.PENDING:
        return <PendingQuote isManualQuote={LSData?.manualQuote} quoteLink={quoteLink} totals={totals} />;
      case SERVICE_SUMMARY_VARIANTS.APPROVED:
        return <ApprovedQuote quoteLink={quoteLink} totals={totals} isManualQuote={LSData?.manualQuote} />;
      case SERVICE_SUMMARY_VARIANTS.REJECTED:
        return <RejectedQuote quoteLink={quoteLink} totals={totals} isManualQuote={LSData?.manualQuote} />;
      case SERVICE_SUMMARY_VARIANTS.CANCELLED:
      case SERVICE_SUMMARY_VARIANTS.ORDER_SUMMARY:
        return <OrderSummary totals={totals} orderId={id} />;

      default:
        return null;
    }
  };

  const isRejectedOrCancelled =
    variant === SERVICE_SUMMARY_VARIANTS.REJECTED || variant === SERVICE_SUMMARY_VARIANTS.CANCELLED;
  const isEditButtonDisabled = isRejectedOrCancelled || !editPricesPermissions.edit;

  const handleEditOrderOrService = () => {
    if (orderData) {
      history.push(`/order/price-edition/${id}`);
      return;
    }
    history.push(`/logistic-service/edit/${id}`);
  };

  return (
    <section className="serviceSummary">
      <div className="tittleWrapper">
        <h3 className="title">{summaryTitle}</h3>
        {variant !== SERVICE_SUMMARY_VARIANTS.TO_QUOTE && editPricesPermissions.read && (
          <NudosButton
            buttonText="Editar información"
            colorPalette="secondary"
            buttonIcon={
              <BordOneToneIcon variant="edit" customWidth="12px" stroke={isRejectedOrCancelled ? 'gray' : 'black'} />
            }
            customClassName="generateQuoteButton"
            handleClick={handleEditOrderOrService}
            isButtonDisabled={isEditButtonDisabled}
          />
        )}
      </div>
      {renderBanner()}
    </section>
  );
};

export default SummaryBanner;
